import type { CheckpointState } from '@ofmos/shared/checklist';
import type { FulfillmentConfiguration, StepActor } from '@ofmos/shared/fulfillment-configuration';
import type { StepMarkState } from '../../../ui';
import { deadlineSentence } from '../../record/checklist';
import { FULFILLMENT } from './configuration';

export type { StepActor };

/**
 * WHO MUST ACT ON EACH ONBOARDING STEP, AND WHAT ITS CELL SAYS (redesign register D4, A9,
 * A10, R-D1; brief FE-FOUNDATION section 3).
 *
 * Who acts on a step is the configuration's (`FulfillmentConfiguration.onboardingSteps`, its
 * `actor`; in OFMOS register A9: the client acts on "Intake filled" and "Notes on one video",
 * the team on the other seven). Keyed by the step's api name, and a field outside the
 * configuration reads as ours: a step somebody adds later is owed by the team until somebody
 * says otherwise. The actor is not in the checkpoint setting (ledger FE-FND DECISION): the
 * setting is the platform's deadline rule, and who acts is the installation's reading of it.
 */
export function stepActors(
  config: FulfillmentConfiguration = FULFILLMENT,
): Readonly<Record<string, StepActor>> {
  return Object.fromEntries(config.onboardingSteps.map((step) => [step.field, step.actor]));
}

/** This build's actors, by step api name. */
export const STEP_ACTORS: Readonly<Record<string, StepActor>> = stepActors();

export function stepActorOf(
  fieldName: string,
  config: FulfillmentConfiguration = FULFILLMENT,
): StepActor {
  return config.onboardingSteps.find((step) => step.field === fieldName)?.actor ?? 'us';
}

export interface StepMarkView {
  readonly state: StepMarkState;
  /** The line under the mark, or null for an open or done step. */
  readonly words: string | null;
}

/**
 * The cell for one step: its checkpoint state, its api name and, for a blocked step, the api
 * name of the field that blocks it (the checkpoint's `blockedByFieldId`, resolved by the page).
 *
 * Words come from `deadlineSentence`, the record page's own sentences, so a cell and a
 * checklist row cannot disagree about "3 days late"; in a cell a waived step says "Not needed"
 * alone (the full waiver sentence stays on checklist rows, ledger R2-C-4). A step blocked by a
 * step that has configured `blockedWords` says those instead ("After the call" in OFMOS,
 * register O4).
 */
export function stepMarkOf(
  state: CheckpointState,
  fieldName: string,
  blockedByFieldName: string | null = null,
  config: FulfillmentConfiguration = FULFILLMENT,
): StepMarkView {
  switch (state.status) {
    case 'done':
      return { state: 'done', words: null };
    case 'waived':
      return { state: 'waived', words: deadlineSentence(state) };
    case 'blocked':
      return {
        state: 'blocked',
        words:
          config.onboardingSteps.find((step) => step.field === blockedByFieldName)?.blockedWords ??
          deadlineSentence(state),
      };
    case 'late':
      return {
        state: stepActorOf(fieldName, config) === 'client' ? 'late-client' : 'late-us',
        words: deadlineSentence(state),
      };
    case 'due':
      return state.days === 0
        ? { state: 'due-today', words: deadlineSentence(state) }
        : { state: 'open', words: null };
  }
}
